import { Component, type ReactElement } from "react";
import { NumericFormat } from "react-number-format";
import { connect } from "react-redux";
import { Copyable, HStack } from "widgets";

import { type Contig, type Range } from "@/graphql.ts";
import { clearContigSearch } from "@/redux/modules/contig-search.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { GENOME_BROWSER_SIZE_OPTIONS, KEYCODE } from "@/settings/settings.js";
import { Addon } from "@/ui-modules/core/redux-form/Addon.js";

import { TooltipController } from "../core/overlays/components/OverlayControllers.js";
import { ContigSelector } from "./ContigSelector.js";

interface IContigRangeSelectorOwnProps {
  // props from the parent entity
  genomeId?: number;
  isVirtualGenome: boolean;
  contig?: Contig;
  range?: Range;
  minRangeLength: number;
  size: string; // for responsive design
  onRangeChange: (range: Range) => void;
  onContigChange: (contig: Contig) => void;
  contigSelectorWidth: number;
}

interface IContigRangeSelectorAppStateProps {
  isLoadingContigs: boolean;
  searchedContigs: Contig[];
  searchTerm: string;
  limit: number;
}

interface IContigRangeSelectorDispatchProps {
  clearContigSearch: () => void;
}

export interface IContigRangeSelectorProps
  extends IContigRangeSelectorOwnProps,
    IContigRangeSelectorAppStateProps,
    IContigRangeSelectorDispatchProps {}

interface IContigRangeSelectorState {
  rangeStart?: number;
  rangeStop?: number;
}

function mapStateToProps(state: IAppState): IContigRangeSelectorAppStateProps {
  const contigSearch = state.contigSearch;
  return {
    isLoadingContigs: contigSearch.isLoading,
    searchedContigs: contigSearch.contigs,
    searchTerm: contigSearch.searchTerm,
    limit: contigSearch.limit,
  };
}

function mapDispatchToProps(dispatch): IContigRangeSelectorDispatchProps {
  return {
    clearContigSearch: () => dispatch(clearContigSearch()),
  };
}

class ContigRangeSelector extends Component<IContigRangeSelectorProps, IContigRangeSelectorState> {
  constructor(props) {
    super(props);
    this.state = {
      rangeStart: props.range?.start,
      rangeStop: props.range?.stop,
    };

    this.handleStartKeyPress = this.handleStartKeyPress.bind(this);
    this.handleStopKeyPress = this.handleStopKeyPress.bind(this);
    this.handleStartChange = this.handleStartChange.bind(this);
    this.handleStopChange = this.handleStopChange.bind(this);
  }

  componentDidMount() {
    this.props.clearContigSearch();
  }

  componentWillUnmount() {
    this.props.clearContigSearch();
  }

  static getDerivedStateFromProps(props: IContigRangeSelectorProps) {
    return {
      rangeStart: props.range?.start,
      rangeStop: props.range?.stop,
    };
  }

  // remove everything but numbers/figures
  private cleanupAndConvertToNumber(value): number {
    return value.replace(/\D/g, "");
  }

  private handleStartChange(event: any) {
    let newValue: number = this.cleanupAndConvertToNumber(event.target.value);

    newValue = Math.max(newValue, 1);
    newValue = Math.min(newValue, this.props.range.stop - this.props.minRangeLength);

    const newRange: Range = {
      start: newValue,
      stop: this.props.range.stop,
    };
    this.props.onRangeChange(newRange);
  }

  // pressing <enter> key modifies the range and calls the callback
  private handleStopChange(event: any) {
    let newValue: number = this.cleanupAndConvertToNumber(event.target.value);

    newValue = Math.min(newValue, this.props.contig.length);
    newValue = Math.max(newValue, this.props.range.start + this.props.minRangeLength);

    const newRange: Range = {
      start: this.props.range.start,
      stop: newValue,
    };
    this.props.onRangeChange(newRange);
  }

  // pressing <enter> key modifies the range and calls the callback
  private handleStartKeyPress(event: any) {
    if (event.charCode === KEYCODE.RETURN) {
      this.handleStartChange(event);
    }
  }

  // pressing <enter> key modifies the range and calls the callback
  private handleStopKeyPress(event: any) {
    if (event.charCode === KEYCODE.RETURN) {
      this.handleStopChange(event);
    }
  }

  render(): ReactElement {
    const numberFormatProps: any = {
      displayType: "input",
      thousandSeparator: true,
      className: "form-control",
      style: { width: "110px" },
    };

    const addStartStopInputFields: boolean =
      this.state.rangeStart &&
      (this.props.size === GENOME_BROWSER_SIZE_OPTIONS.XLARGE || this.props.size === GENOME_BROWSER_SIZE_OPTIONS.LARGE);

    return (
      <HStack>
        <div style={{ width: this.props.contigSelectorWidth }} data-testid={"range-selector-contig"}>
          <ContigSelector
            onContigChange={this.props.onContigChange}
            genomeId={this.props.genomeId}
            isVirtualGenome={this.props.isVirtualGenome}
            selectedContig={this.props.contig}
          />
        </div>

        {addStartStopInputFields && (
          <>
            <Addon label={"from"} />
            <TooltipController content={"Current window start"}>
              {(tooltipTriggerProps) => (
                <div
                  onPointerEnter={tooltipTriggerProps.onPointerEnter}
                  onPointerLeave={tooltipTriggerProps.onPointerLeave}
                  ref={tooltipTriggerProps.ref}
                  data-testid={"range-selector-start-input"}
                >
                  <NumericFormat
                    value={this.state.rangeStart}
                    onBlur={this.handleStartChange}
                    onKeyPress={this.handleStartKeyPress}
                    {...numberFormatProps}
                    key="contigRangeStart"
                  />
                </div>
              )}
            </TooltipController>
            <Addon label={"to"} />
            <TooltipController content={"Current window end"}>
              {(tooltipTriggerProps) => (
                <div
                  onPointerEnter={tooltipTriggerProps.onPointerEnter}
                  onPointerLeave={tooltipTriggerProps.onPointerLeave}
                  ref={tooltipTriggerProps.ref}
                  data-testid={"range-selector-stop-input"}
                >
                  <NumericFormat
                    value={this.state.rangeStop}
                    onBlur={this.handleStopChange}
                    onKeyPress={this.handleStopKeyPress}
                    {...numberFormatProps}
                    key="contigRangeStop"
                  />
                </div>
              )}
            </TooltipController>

            <TooltipController content={"Copy Coordinates to Clipboard"}>
              {(props) => (
                <div {...props} data-testid={"range-selector-copy-button"}>
                  <Copyable
                    getTextContent={async () =>
                      `${this.props.contig?.sequenceId}:${this.state.rangeStart}-${this.state.rangeStop}`
                    }
                    hideContent={() => true}
                  />
                </div>
              )}
            </TooltipController>
          </>
        )}
      </HStack>
    );
  }
}

export default connect<
  IContigRangeSelectorAppStateProps,
  IContigRangeSelectorDispatchProps,
  IContigRangeSelectorOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ContigRangeSelector);
